/**
 * The typed client for the post-quantum license key API (license-key-cards task 14.1; requirements
 * 11.3, 11.4, 11.5, 7.9, 8.7, 18.1). The shared `LicenseCard` drives one set of calls per scope:
 *
 *   - `tenant` -> `/api/v1/team/license*`    the team's production Tenant_Key (admin only)
 *   - `user`   -> `/api/v1/account/license*` the signed-in member's own development User_Key
 *
 * Both sets live under `/api/v1`, so this builds on the shared {@link apiFetch} (same origin,
 * cookie-borne session, the shared {@link ApiError} classes, and the 503 -> `waking` mapping).
 *
 * TENANCY. No call carries a tenant id or a user id: the server takes both from the session. The
 * only inputs are an idempotency `op_id`, the `expected_license_id` of the key being replaced or
 * revoked, and an optional `mfa_code` for step-up.
 *
 * KEY HANDLING (Requirement 11.5). A License_Key is a secret. ONLY {@link getLicenseStatus} is meant
 * for TanStack Query: its answer carries metadata and the masked form, never the key. The four
 * key-bearing calls ({@link generateLicense}, {@link regenerateLicense}, {@link revealLicense}, and
 * {@link revokeLicense} which returns no key) are PLAIN async functions to be called from event
 * handlers, and this module never stores a key anywhere (no module state, no cache, no storage), so
 * a key cannot enter the query or mutation cache through it.
 *
 * ERRORS. Failures are the shared {@link ApiError}, unchanged. The server's fixed `code` is on
 * `error.code`: `forbidden` (403), `step_up_required` (401), `no_key_holder` | `already_active` |
 * `not_active` (409), `rate_limited` (429, `retryAfterSeconds` from `Retry-After`),
 * `not_configured` (501), `audit_contended` (503, surfaces as `waking`), `internal` (500). Use
 * {@link isStepUpRequired} to branch into the code prompt.
 *
 * The view types below are re-declared (not imported) from `src/worker/license/types.ts`: the web
 * program (`tsconfig.web.json`) has no workerd ambient types and must not pull Worker modules in.
 * No `console` call (Requirement 11.1 spirit).
 */

import { apiFetch, ApiError } from "./api.ts";

// ---------------------------------------------------------------------------------------------
// Types (mirror `src/worker/license/types.ts` view shapes; keep in sync)
// ---------------------------------------------------------------------------------------------

/** Which license: the team's production key or the member's own development key. */
export type LicenseScope = "tenant" | "user";

export type LicenseEnvironment = "production" | "development";

/** `expired` is computed server-side from `expires_at`; the stored status is `active` or `revoked`. */
export type LicenseViewStatus = "active" | "expired" | "revoked";

/** Metadata of one license. Never carries the License_Key. Times are epoch milliseconds. */
export interface LicenseView {
  readonly license_id: string;
  readonly scope: LicenseScope;
  readonly environment: LicenseEnvironment;
  readonly status: LicenseViewStatus;
  readonly issuer_kid: string;
  readonly alg: string;
  readonly issued_at: number;
  readonly expires_at: number;
  readonly revoked_at: number | null;
  readonly client_name: string;
  readonly holder: { readonly id: string; readonly name: string };
  /** `"OATH_" + 16 bullets + last 4 characters`. */
  readonly masked_key: string;
  /** `"3F9A 07C2 B15E 591A"`, derived only from public values. */
  readonly card_number: string;
}

/** The answer of the status read. Never carries the License_Key. */
export interface LicenseStatus {
  /** The stored-active row, whose view status is `active` or `expired`. */
  readonly license: LicenseView | null;
  /** Most recent 10 revoked or expired licenses, newest first. */
  readonly history: readonly LicenseView[];
  /** `false` drives the "not configured" card state. */
  readonly issuing: { readonly configured: boolean };
  /** `false` for a viewer on the account route. */
  readonly can_manage: boolean;
  /** Tenant_Key only: whether the recorded name or holder no longer matches the team. */
  readonly mismatch: { readonly client_name: boolean; readonly key_holder: boolean } | null;
  /** Tenant_Key only: the team's current name and Root_Administrator display name. */
  readonly current: { readonly client_name: string; readonly holder_name: string | null } | null;
}

/** What generate and regenerate resolve to. `licenseKey` is the secret: show it, never cache it. */
export interface IssuedLicense {
  readonly license: LicenseView;
  readonly licenseKey: string;
  /** True when the server recognised a retry of the same `opId` and returned the first result. */
  readonly replayed: boolean;
}

/** Input of {@link generateLicense}. */
export interface GenerateLicenseInput {
  /** From {@link newOpId}: one per user action, reused for retries of that action (7.9). */
  readonly opId: string;
  /** The 6-digit TOTP code, sent after a `step_up_required` answer. */
  readonly mfaCode?: string;
}

/** Input of {@link regenerateLicense} and {@link revokeLicense}. */
export interface ReplaceLicenseInput extends GenerateLicenseInput {
  /** The `license_id` the user saw; a mismatch answers `not_active` instead of acting on another key. */
  readonly expectedLicenseId: string;
}

/** Input of {@link revealLicense}. */
export interface RevealLicenseInput {
  readonly mfaCode?: string;
}

/** The success envelope (`{ status, message, data }`). */
interface Envelope<T> {
  readonly status?: boolean;
  readonly message?: string;
  readonly data?: T | null;
}

// ---------------------------------------------------------------------------------------------
// Paths, keys and ids
// ---------------------------------------------------------------------------------------------

/** Path of each scope, relative to `/api/v1` (which {@link apiFetch} prepends). */
const BASE: Readonly<Record<LicenseScope, string>> = {
  tenant: "/team/license",
  user: "/account/license",
};

/** The TanStack Query key for a scope's status, per team so a team switch re-points the card. */
export function licenseQueryKey(scope: LicenseScope, teamId: string): readonly ["license", LicenseScope, string] {
  return ["license", scope, teamId] as const;
}

/**
 * A fresh idempotency key: 16 random bytes as 22 unpadded base64url characters. It satisfies the
 * server's `[A-Za-z0-9_-]{16,40}` shape. Call it once per user action and reuse the value when
 * retrying that same action, so the server can recognise a replay.
 */
export function newOpId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

/** True for the 401 `step_up_required` answer: ask for a TOTP code and retry the same action. */
export function isStepUpRequired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.code === "step_up_required";
}

// ---------------------------------------------------------------------------------------------
// Response shaping
// ---------------------------------------------------------------------------------------------

function malformed(what: string): ApiError {
  return new ApiError("malformed", `The server returned an unexpected ${what} response.`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** True when `value` has the fields the card reads from a license view. */
function isLicenseView(value: unknown): value is LicenseView {
  if (!isRecord(value)) return false;
  const holder = value["holder"];
  return (
    typeof value["license_id"] === "string" &&
    (value["scope"] === "tenant" || value["scope"] === "user") &&
    (value["environment"] === "production" || value["environment"] === "development") &&
    (value["status"] === "active" || value["status"] === "expired" || value["status"] === "revoked") &&
    typeof value["issuer_kid"] === "string" &&
    typeof value["alg"] === "string" &&
    typeof value["issued_at"] === "number" &&
    typeof value["expires_at"] === "number" &&
    typeof value["client_name"] === "string" &&
    isRecord(holder) &&
    typeof holder["id"] === "string" &&
    typeof holder["name"] === "string" &&
    typeof value["masked_key"] === "string" &&
    typeof value["card_number"] === "string"
  );
}

/** Shape the status `data` or throw `malformed` (fail closed rather than render a half card). */
function parseStatus(data: unknown): LicenseStatus {
  if (!isRecord(data)) throw malformed("license status");
  const license = data["license"] ?? null;
  const history = data["history"];
  const issuing = data["issuing"];
  if (license !== null && !isLicenseView(license)) throw malformed("license status");
  if (!Array.isArray(history) || !history.every(isLicenseView)) throw malformed("license status");
  if (!isRecord(issuing) || typeof issuing["configured"] !== "boolean") throw malformed("license status");

  const mismatchRaw = data["mismatch"];
  const mismatch =
    isRecord(mismatchRaw)
      ? {
          client_name: mismatchRaw["client_name"] === true,
          key_holder: mismatchRaw["key_holder"] === true,
        }
      : null;
  const currentRaw = data["current"];
  const current =
    isRecord(currentRaw) && typeof currentRaw["client_name"] === "string"
      ? {
          client_name: currentRaw["client_name"],
          holder_name: typeof currentRaw["holder_name"] === "string" ? currentRaw["holder_name"] : null,
        }
      : null;

  return {
    license,
    history: history as LicenseView[],
    issuing: { configured: issuing["configured"] },
    can_manage: data["can_manage"] === true,
    mismatch,
    current,
  };
}

/** Shape a generate/regenerate `data` (`{ license, license_key, replayed? }`) or throw `malformed`. */
function parseIssued(data: unknown): IssuedLicense {
  if (!isRecord(data)) throw malformed("license");
  const license = data["license"];
  const key = data["license_key"];
  if (!isLicenseView(license) || typeof key !== "string" || key.length === 0) throw malformed("license");
  return { license, licenseKey: key, replayed: data["replayed"] === true };
}

/** Optional `mfa_code`, sent only when a non-empty code was given. */
function mfaMember(mfaCode: string | undefined): { mfa_code?: string } {
  return mfaCode !== undefined && mfaCode !== "" ? { mfa_code: mfaCode } : {};
}

// ---------------------------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------------------------

/**
 * Read a scope's license status and history (`GET /api/v1/{team|account}/license`). Safe to cache:
 * the answer has metadata and the masked key only. This is the ONE call meant for TanStack Query.
 */
export async function getLicenseStatus(scope: LicenseScope, signal?: AbortSignal): Promise<LicenseStatus> {
  const body = await apiFetch<Envelope<unknown>>(BASE[scope], {
    ...(signal === undefined ? {} : { signal }),
  });
  return parseStatus(body?.data ?? null);
}

/** Generate a license (`POST .../license`). Resolves with the key, shown once; never cache it. */
export async function generateLicense(
  scope: LicenseScope,
  input: GenerateLicenseInput,
  signal?: AbortSignal,
): Promise<IssuedLicense> {
  const body = await apiFetch<Envelope<unknown>>(BASE[scope], {
    method: "POST",
    body: { op_id: input.opId, ...mfaMember(input.mfaCode) },
    ...(signal === undefined ? {} : { signal }),
  });
  return parseIssued(body?.data ?? null);
}

/** Replace the active license (`POST .../license/regenerate`). The old key is revoked as `replaced`. */
export async function regenerateLicense(
  scope: LicenseScope,
  input: ReplaceLicenseInput,
  signal?: AbortSignal,
): Promise<IssuedLicense> {
  const body = await apiFetch<Envelope<unknown>>(`${BASE[scope]}/regenerate`, {
    method: "POST",
    body: {
      op_id: input.opId,
      expected_license_id: input.expectedLicenseId,
      ...mfaMember(input.mfaCode),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return parseIssued(body?.data ?? null);
}

/** Revoke the active license (`POST .../license/revoke`). Resolves with no key. */
export async function revokeLicense(
  scope: LicenseScope,
  input: ReplaceLicenseInput,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<Envelope<unknown>>(`${BASE[scope]}/revoke`, {
    method: "POST",
    body: {
      op_id: input.opId,
      expected_license_id: input.expectedLicenseId,
      ...mfaMember(input.mfaCode),
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Fetch the active license key (`POST .../license/reveal`) for display or copy. Resolves with the
 * key string; the caller holds it in component state only and clears it on Hide or unmount.
 */
export async function revealLicense(
  scope: LicenseScope,
  input: RevealLicenseInput = {},
  signal?: AbortSignal,
): Promise<string> {
  const body = await apiFetch<Envelope<unknown>>(`${BASE[scope]}/reveal`, {
    method: "POST",
    body: { ...mfaMember(input.mfaCode) },
    ...(signal === undefined ? {} : { signal }),
  });
  const data = body?.data ?? null;
  const key = isRecord(data) ? data["license_key"] : undefined;
  if (typeof key !== "string" || key.length === 0) throw malformed("license");
  return key;
}
